import { useCallback, useState } from 'react';
import {
    DocumentNode,
    OperationVariables,
    TypedDocumentNode,
    useMutation,
    useQuery,
} from '@apollo/client';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';

type RowWithId = {
    id: string;
};

type DeleteSuccessCopy = {
    title: string;
    message: string;
};

type UseVatConfigCrudOptions<TRow extends RowWithId, TData> = {
    query: DocumentNode | TypedDocumentNode<TData, OperationVariables>;
    deleteMutation: DocumentNode | TypedDocumentNode<unknown, { id: string }>;
    getRows: (data: TData | undefined) => TRow[];
    getDeleteSuccessCopy: (row: TRow) => DeleteSuccessCopy;
    deleteErrorMessage: string;
    isDeleteSuccessful?: (data: Record<string, unknown>) => boolean;
};

function defaultIsDeleteSuccessful(data: Record<string, unknown>): boolean {
    const payload = Object.values(data)[0];
    if (typeof payload === 'boolean') {
        return payload;
    }
    return payload != null;
}

export function useVatConfigCrud<TRow extends RowWithId, TData>({
    query,
    deleteMutation,
    getRows,
    getDeleteSuccessCopy,
    deleteErrorMessage,
    isDeleteSuccessful = defaultIsDeleteSuccessful,
}: UseVatConfigCrudOptions<TRow, TData>) {
    const [formModalOpened, formModalHandlers] = useDisclosure(false);
    const [deleteModalOpened, deleteModalHandlers] = useDisclosure(false);
    const [selectedConfig, setSelectedConfig] = useState<TRow | undefined>(
        undefined,
    );

    const { data, loading, error } = useQuery<TData>(query);

    const [deleteConfig, { loading: deleting }] = useMutation(deleteMutation, {
        refetchQueries: [{ query }],
    });

    const rows = getRows(data);

    const handleAdd = useCallback(() => {
        setSelectedConfig(undefined);
        formModalHandlers.open();
    }, [formModalHandlers]);

    const handleEdit = useCallback(
        (config: TRow) => {
            setSelectedConfig(config);
            formModalHandlers.open();
        },
        [formModalHandlers],
    );

    const handleDeleteRequest = useCallback(
        (config: TRow) => {
            setSelectedConfig(config);
            deleteModalHandlers.open();
        },
        [deleteModalHandlers],
    );

    const handleDeleteConfirm = useCallback(async () => {
        if (!selectedConfig) {
            return;
        }

        const successCopy = getDeleteSuccessCopy(selectedConfig);
        const result = await deleteConfig({
            variables: { id: selectedConfig.id },
        }).catch(() => null);

        const deleteSucceeded =
            result?.data != null &&
            isDeleteSuccessful(result.data as Record<string, unknown>);

        if (!deleteSucceeded) {
            notifications.show({
                title: 'Error',
                message: deleteErrorMessage,
                color: 'red',
            });
            deleteModalHandlers.close();
            setSelectedConfig(undefined);
            return;
        }

        notifications.show({
            title: successCopy.title,
            message: successCopy.message,
            color: 'green',
        });
        deleteModalHandlers.close();
        setSelectedConfig(undefined);
    }, [
        deleteConfig,
        deleteErrorMessage,
        deleteModalHandlers,
        getDeleteSuccessCopy,
        isDeleteSuccessful,
        selectedConfig,
    ]);

    return {
        rows,
        loading,
        error,
        deleting,
        selectedConfig,
        formModalOpened,
        formModalHandlers,
        deleteModalOpened,
        deleteModalHandlers,
        handleAdd,
        handleEdit,
        handleDeleteRequest,
        handleDeleteConfirm,
    };
}
